import { NavLink } from 'react-router-dom';
import { useMe } from '../hooks/useAuth.js';
import { useUnreadCount } from '../hooks/useMessages.js';

// Mobile-only bottom tab bar. Hidden on `lg` (Tailwind ≥ 1024px) and up where
// the regular top nav takes over.
export function BottomTabBar() {
  const { data } = useMe();
  const signedIn = Boolean(data?.user);
  const unread = useUnreadCount();
  const unreadCount = unread.data?.count ?? 0;

  const tabs = [
    { to: '/', label: 'Home', icon: HomeIcon, end: true },
    { to: '/search', label: 'Search', icon: SearchIcon },
    signedIn
      ? { to: '/inbox', label: 'Inbox', icon: InboxIcon }
      : { to: '/list-your-trailer', label: 'List', icon: TrailerIcon },
    {
      to: signedIn ? '/account' : '/login',
      label: signedIn ? 'Account' : 'Sign in',
      icon: UserIcon,
    },
  ];

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-navy-100 pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      <div className="flex justify-around items-stretch h-16">
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={'end' in t ? t.end : undefined}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium min-w-[44px] ${
                isActive ? 'text-orange' : 'text-navy-500 hover:text-navy'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="relative">
                  <t.icon active={isActive} />
                  {t.to === '/inbox' && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-2 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                <span>{t.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" stroke={active ? '#fff' : 'currentColor'} />
    </svg>
  );
}

function SearchIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={active ? 2.5 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );
}

function InboxIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path
        d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"
        stroke={active ? '#fff' : 'currentColor'}
      />
    </svg>
  );
}

function UserIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" stroke={active ? '#fff' : 'currentColor'} />
    </svg>
  );
}

function TrailerIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="8" width="16" height="9" rx="1.5" />
      <circle cx="7" cy="19" r="2" />
      <circle cx="14" cy="19" r="2" />
      <path d="M18 12h3l-1.5 -2.5z" />
    </svg>
  );
}
